import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useMatchSocket } from "@/lib/useMatchSocket";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX, Tv, ArrowLeft, Radio } from "lucide-react";
import { enableMatchAudio, disableMatchAudio, playCue } from "@/lib/matchAudio";
import PushSubscribeButton from "@/components/PushSubscribeButton";
import { PadelLiveScore, PadelFinalScore, derivePadelScore } from "@/components/PadelScore";

/**
 * iter89 · Live Broadcast page — rebuilt around the referee's padel-format
 * scoring. The previous time-based engine (match clock, periods, cumulative
 * point counter) is gone; this page now reads exclusively from `set_scores`,
 * `tennis_display` and `total_sets_won_a/b` — the same fields the referee
 * writes to via `/matches/{id}/tennis/state`.
 *
 * Deduplication rule: the match picker collapses on `tie_id` (fallback to
 * `court + team pair`), so each tie appears exactly once no matter how many
 * seed matches it carries.
 */

const STAGE_LABEL = {
  group: "Round Robin",
  quarter: "Quarter-Final",
  semi: "Semi-Final",
  final: "FINAL",
};
const SEED_ACCENT = { 1: "#F4B400", 2: "#00A86B", 3: "#7DD3FC" };

export default function PublicMatch() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    const load = () =>
      api.get("/public/matches").then(({ data }) => setMatches(data)).catch(() => {});
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, []);

  // Group matches by tie so each tie renders one tab. Fallback to
  // `court|team_a_id|team_b_id` for legacy matches without a tie_id.
  const tieGroups = useMemo(() => groupIntoTies(matches), [matches]);

  // The tie currently selected on-screen is the one containing the matchId in the URL.
  const selectedTie = useMemo(
    () => tieGroups.find((g) => g.matches.some((m) => m.id === matchId)),
    [tieGroups, matchId]
  );

  useEffect(() => {
    if (matchId || tieGroups.length === 0) return;
    // Default to first live tie, else first tie in the list.
    const target = tieGroups.find((g) => g.status === "live") || tieGroups[0];
    const pick = pickSeedForTie(target);
    if (pick) nav(`/watch/${pick.id}`, { replace: true });
  }, [matchId, tieGroups, nav]);

  return (
    <div className="min-h-screen bg-[#060d09] flex flex-col">
      <div className="px-4 md:px-6 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap">
        <Link
          to="/live"
          data-testid="watch-back"
          className="text-zinc-400 hover:text-white flex items-center gap-1 text-xs uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Live Hub
        </Link>
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">Tie</span>
        <div className="flex gap-1.5 flex-wrap" data-testid="watch-tie-picker">
          {tieGroups.map((g) => {
            const seed = pickSeedForTie(g);
            const isActive =
              selectedTie && selectedTie.key === g.key;
            return (
              <button
                key={g.key}
                data-testid={`watch-tie-${g.key}`}
                onClick={() => seed && nav(`/watch/${seed.id}`)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors flex items-center gap-2 ${
                  isActive
                    ? "border-[#00A86B] text-[#00A86B] bg-[#00A86B]/10"
                    : "border-white/10 text-zinc-300 hover:text-white hover:border-white/25"
                }`}
              >
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {g.court || "—"}
                  {g.group ? ` · Grp ${g.group}` : ""}
                </span>
                <span className="font-bold">
                  {g.team_a_name} vs {g.team_b_name}
                </span>
                {g.status === "live" && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                )}
                {g.status === "finished" && (
                  <span className="text-[9px] uppercase text-zinc-500">FT</span>
                )}
              </button>
            );
          })}
          {tieGroups.length === 0 && (
            <span className="text-xs text-zinc-600">No matches scheduled yet.</span>
          )}
        </div>
      </div>
      {selectedTie ? (
        <TieBroadcast tie={selectedTie} activeMatchId={matchId} onNav={(id) => nav(`/watch/${id}`)} />
      ) : (
        <div
          className="flex-1 flex items-center justify-center text-zinc-500"
          data-testid="watch-empty"
        >
          <Tv className="w-10 h-10 mr-3 text-zinc-700" /> Select a tie to watch live.
        </div>
      )}
    </div>
  );
}

/** Group matches by tie for the picker. */
function groupIntoTies(matches) {
  const groups = new Map();
  for (const m of matches) {
    const key =
      m.tie_id ||
      `${m.court || "?"}|${[m.team_a_id, m.team_b_id].sort().join("|")}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        tie_id: m.tie_id || null,
        court: m.court,
        group: m.group || (m.team_a && m.team_a.group) || null,
        team_a_id: m.team_a_id,
        team_b_id: m.team_b_id,
        team_a_name: m.team_a_name || m.team_a?.name || "Team A",
        team_b_name: m.team_b_name || m.team_b?.name || "Team B",
        status: "scheduled",
        matches: [],
      };
      groups.set(key, g);
    }
    g.matches.push(m);
    if (m.status === "live") g.status = "live";
    else if (m.status === "finished" && g.status !== "live") g.status = "finished";
  }
  const list = Array.from(groups.values());
  // Ensure each tie's matches are sorted by seed.
  for (const g of list) g.matches.sort((a, b) => (a.seed || 9) - (b.seed || 9));
  // Order picker: live first, then scheduled, then finished; within each, by court name.
  const order = { live: 0, scheduled: 1, finished: 2 };
  list.sort(
    (a, b) =>
      (order[a.status] ?? 3) - (order[b.status] ?? 3) ||
      (a.court || "").localeCompare(b.court || "") ||
      (a.team_a_name || "").localeCompare(b.team_a_name || "")
  );
  return list;
}

/** Which seed match should we deep-link into for this tie? Live → first live,
 *  else Seed 1 (default entry), else the first match on file. */
function pickSeedForTie(g) {
  if (!g) return null;
  const live = g.matches.find((m) => m.status === "live");
  if (live) return live;
  const scheduled = g.matches.find((m) => m.status !== "finished");
  if (scheduled) return scheduled;
  return g.matches[0] || null;
}

/**
 * Renders the whole tie in a single view: hero card for the active seed
 * match (big padel scoreboard with LIVE badge) + a strip of the other two
 * seed match summaries below. Live SSE updates fire via `useMatchSocket`
 * inside each seed card.
 */
function TieBroadcast({ tie, activeMatchId, onNav }) {
  const activeMatch =
    tie.matches.find((m) => m.id === activeMatchId) || tie.matches[0];
  return (
    <div className="flex-1 score-grid-bg flex flex-col" data-testid="watch-scoreboard">
      {/* header */}
      <div className="px-4 md:px-6 py-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            {activeMatch.court} · {STAGE_LABEL[activeMatch.stage] || activeMatch.stage}
            {tie.group ? ` · Group ${tie.group}` : ""}
          </div>
          <div className="font-anton uppercase text-xl md:text-3xl text-white tracking-tight">
            {tie.team_a_name}
            <span className="text-zinc-600 mx-3">vs</span>
            {tie.team_b_name}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SoundToggle />
          <PushSubscribeButton className="h-9 text-xs" />
        </div>
      </div>

      {/* Hero — the active seed match */}
      <SeedHeroCard match={activeMatch} teamAName={tie.team_a_name} teamBName={tie.team_b_name} />

      {/* Other seed matches (compact) */}
      <div className="grid md:grid-cols-3 gap-px bg-white/5 border-t border-white/10">
        {tie.matches.map((m) => (
          <button
            key={m.id}
            onClick={() => onNav(m.id)}
            className={`text-left p-4 bg-[#0b1a12] hover:bg-[#12241c] transition-colors ${
              m.id === activeMatch.id ? "ring-2 ring-inset ring-[#00A86B]/60" : ""
            }`}
            data-testid={`watch-seed-strip-${m.id}`}
          >
            <SeedStripCard match={m} teamAName={tie.team_a_name} teamBName={tie.team_b_name} />
          </button>
        ))}
      </div>
    </div>
  );
}

/** Big centrepiece card for the active seed match. */
function SeedHeroCard({ match, teamAName, teamBName }) {
  const { state: live } = useMatchSocket(match.id);
  const data = live || match;
  const seed = data.seed || data.seed_number;
  const accent = SEED_ACCENT[seed] || "#94a3b8";
  const status = (data.status || "scheduled").toLowerCase();
  const { setsA, setsB, finished, inProgress, pts } = derivePadelScore(data);
  const currentSetIdx = finished.length + (inProgress ? 1 : 0) || 1;

  return (
    <div
      className="p-6 md:p-10 flex-1 relative"
      style={{ borderTop: `4px solid ${accent}` }}
      data-testid={`watch-hero-${match.id}`}
    >
      {/* Status chip + seed badge */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <span
          className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
          style={{ background: `${accent}22`, color: accent }}
        >
          Seed {seed}
        </span>
        <span
          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded flex items-center gap-1 ${
            status === "live"
              ? "bg-red-600 text-white"
              : status === "finished"
              ? "bg-emerald-600/20 text-emerald-300 border border-emerald-500/40"
              : "bg-white/5 text-zinc-400 border border-white/10"
          }`}
          data-testid={`watch-status-${match.id}`}
        >
          {status === "live" && (
            <Radio className="w-3 h-3 animate-pulse" />
          )}
          {status === "live" ? "Live" : status === "finished" ? "Completed" : "Not Started"}
        </span>
      </div>

      {/* Big padel scoreboard */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-6 md:gap-10">
        <TeamBlock name={teamAName} side="a" setsWon={setsA} />
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Sets</div>
          <div className="font-anton text-6xl md:text-8xl tabular-nums text-white leading-none">
            {setsA}
            <span className="text-zinc-700 mx-3">·</span>
            {setsB}
          </div>
          {status !== "finished" && (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-3">
                Set {currentSetIdx} · Games
              </div>
              <div className="font-anton text-3xl md:text-5xl tabular-nums text-amber-300 leading-none">
                {inProgress?.a ?? finished[finished.length - 1]?.a ?? 0}
                <span className="text-zinc-700 mx-2">-</span>
                {inProgress?.b ?? finished[finished.length - 1]?.b ?? 0}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-3">
                Current Game
              </div>
              <div className="font-anton text-2xl md:text-4xl tabular-nums text-white leading-none">
                {pts.a}
                <span className="text-zinc-700 mx-2">·</span>
                {pts.b}
              </div>
              {pts.label && (
                <div className="text-[10px] uppercase tracking-widest font-bold text-red-300">
                  {pts.label}
                </div>
              )}
            </>
          )}
        </div>
        <TeamBlock name={teamBName} side="b" setsWon={setsB} align="right" />
      </div>

      {/* Set-by-set breakdown row */}
      {(finished.length > 0 || inProgress) && (
        <div
          className="mt-8 flex items-center justify-center gap-3 text-xs uppercase tracking-widest text-zinc-400"
          data-testid={`watch-set-line-${match.id}`}
        >
          <span className="text-zinc-500">Sets</span>
          {[...finished, ...(inProgress ? [inProgress] : [])].map((s, i) => {
            const isLive = inProgress && i === finished.length;
            const winner = !isLive && s.a > s.b ? "a" : !isLive && s.b > s.a ? "b" : null;
            const color = winner ? "text-emerald-300" : isLive ? "text-amber-300" : "text-white";
            return (
              <span
                key={i}
                className={`font-anton text-lg tabular-nums ${color}`}
                data-testid={`watch-set-cell-${match.id}-${i + 1}`}
              >
                {s.a}-{s.b}
                {isLive && (
                  <span className="ml-1 text-[9px] font-bold text-red-300">•LIVE</span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeamBlock({ name, side, setsWon, align = "left" }) {
  return (
    <div className={`text-${align === "right" ? "right" : "left"}`}>
      <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-1">
        Team {side.toUpperCase()}
      </div>
      <div
        className="font-anton text-2xl md:text-5xl uppercase text-white tracking-tighter truncate"
        data-testid={`watch-team-${side}`}
      >
        {name}
      </div>
      <div className={`mt-3 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2 ${align === "right" ? "justify-end" : ""}`}>
        {align === "right" && <span className="font-anton text-3xl text-[#00A86B] tabular-nums">{setsWon}</span>}
        <span>Sets won</span>
        {align !== "right" && <span className="font-anton text-3xl text-[#00A86B] tabular-nums">{setsWon}</span>}
      </div>
    </div>
  );
}

/** Compact strip for the two non-active seed matches. */
function SeedStripCard({ match, teamAName, teamBName }) {
  const { state: live } = useMatchSocket(match.id);
  const data = live || match;
  const seed = data.seed || data.seed_number;
  const status = (data.status || "scheduled").toLowerCase();
  const accent = SEED_ACCENT[seed] || "#94a3b8";
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
          style={{ background: `${accent}22`, color: accent }}
        >
          Seed {seed}
        </span>
        <span
          className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${
            status === "live"
              ? "bg-red-600 text-white"
              : status === "finished"
              ? "text-emerald-300 bg-emerald-500/10 border border-emerald-500/30"
              : "text-zinc-400 bg-white/5 border border-white/10"
          }`}
        >
          {status === "live" ? "Live" : status === "finished" ? "Completed" : "Not Started"}
        </span>
      </div>
      {status === "finished" ? (
        <PadelFinalScore m={data} teamAName={teamAName} teamBName={teamBName} />
      ) : (
        <PadelLiveScore m={data} teamAName={teamAName} teamBName={teamBName} dense />
      )}
    </div>
  );
}

function SoundToggle() {
  const [on, setOn] = useState(false);
  return (
    <Button
      data-testid="watch-sound-toggle"
      onClick={() => {
        if (on) {
          disableMatchAudio();
          setOn(false);
        } else {
          setOn(enableMatchAudio());
        }
      }}
      variant="outline"
      size="sm"
      className={`border-white/20 ${on ? "text-[#00A86B]" : "text-zinc-400"}`}
    >
      {on ? <Volume2 className="w-4 h-4 mr-1.5" /> : <VolumeX className="w-4 h-4 mr-1.5" />}
      {on ? "Sound On" : "Enable Sound"}
    </Button>
  );
}
